// MaterialReservationDetailModal.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    Divider,
} from 'antd';
import { CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import './MaterialReservationDetailModal.css';
import { getConfig } from '../../../config';
import { HARD_CODED_DIM_SUGGESTIONS as RAW_SUGGESTIONS } from './dimSuggestions';

const { Title } = Typography;
const { Option, OptGroup } = Select;

const { API_BASE_URL } = getConfig();
const AUTH_TOKEN = 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU';

type Designation = { name: string };
type Material = { name: string; materialGrades: string[]; densityFactorKgPerMPerMm2: number };
type ProfileType = { name: string; properties: Array<'HEIGHT' | 'WIDTH' | 'THICKNESS' | 'DIAMETER' | 'UNKNOWN'> };
type DimKey = 'HEIGHT' | 'WIDTH' | 'THICKNESS' | 'DIAMETER';

// ==== NEU: differenzierte “Stück”-Unterarten ====
type Uom = 'LM' | 'PCS' | 'PCS_STOREY' | 'PCS_FACADE' | 'PCS_BUILDING';

const UOM_LABEL_MAP: Record<Uom, string> = {
    LM: 'Laufmeter',
    PCS: 'Stück',
    PCS_STOREY: 'Geschosse',
    PCS_FACADE: 'Fassaden',
    PCS_BUILDING: 'Gebäude',
};

// Labels: so wie du es möchtest: "Anzahl Geschosse" etc.
const TOTAL_QTY_LABEL: Record<Uom, string> = {
    LM: 'Gesamtmenge (in Laufmeter)',
    PCS: 'Anzahl Stück',
    PCS_STOREY: 'Anzahl Geschosse',
    PCS_FACADE: 'Anzahl Fassaden',
    PCS_BUILDING: 'Anzahl Gebäude',
};

// Für die Spalte "piecesPerUnit" ebenfalls "Anzahl …" bei PCS_*.
// (LM bleibt fachlich "Stück/Laufmeter")
const PIECES_PER_UNIT_LABEL: Record<Uom, string> = {
    LM: 'Stück/Laufmeter',
    PCS: 'Anzahl Stück',
    PCS_STOREY: 'Stück je Geschoss',
    PCS_FACADE: 'Stück je  Fassaden',
    PCS_BUILDING: 'Stück je  Gebäude',
};

const PIECES_PER_UNIT_EDITOR_LABEL: Record<Uom, string> = {
    LM: 'Stück je Laufmeter',
    PCS: 'Stück je Einheit',
    PCS_STOREY: 'Stück je Geschoss',
    PCS_FACADE: 'Stück je Fassade',
    PCS_BUILDING: 'Stück je Gebäude',
};


const isPCS = (uom: Uom) => uom !== 'LM';

export type MaterialReservationItem = {
    id: string;
    designation?: string;
    material?: string;
    materialGrade?: string;
    profileType?: string;

    // Spezialprofile
    specialProfileName?: string;
    specialWeightPerMeter?: number; // kg/m

    // Maße
    HEIGHT?: number;
    WIDTH?: number;
    THICKNESS?: number;
    DIAMETER?: number;

    // Mengen (je nach Einheit)
    lengthPerUnit?: number; // mm
    piecesPerUnit?: number; // Stück
    weightPerMeter?: number; // kg/m

    // Kommentar pro Artikel
    comment?: string;
};

export type MaterialReservationState = {
    uom: Uom;
    totalQty: number; // EINGABE OBEN (z. B. 200 LFM, 3 Geschosse, 1 Fassade, …)
    items: MaterialReservationItem[];
};

export type MaterialReservationSavePayload = {
    state: MaterialReservationState; // JSON zum Persistieren
    runningMeters: number; // = OBEN eingegebene LFM (bei UoM=LM) ODER Stückzahl (bei PCS_…)
    materialRunningMeters: number; // = Summe Material-LFM über alle Positionen (immer Meter)
    kgPerLm: number; // Gesamtgewicht / materialRunningMeters
    totalWeightKg: number; // Summe Gewicht über alle Positionen
};

type Props = {
    initialState?: MaterialReservationState | null;
    onSave?: (p: MaterialReservationSavePayload) => void;
    onCancel?: () => void;
};

const DIM_LABELS: Record<DimKey, string> = {
    HEIGHT: 'Profilhöhe (mm)',
    WIDTH: 'Profilbreite (mm)',
    THICKNESS: 'Wandstärke (mm)',
    DIAMETER: 'Durchmesser (mm)',
};

const SPECIAL_PROFILE_OPTIONS: string[] = [
    'Ganzglas-U-Profil',
    'Welser H-Profil',
    'Welser U-Profil',
    'C-Profil 40/25/2',
    'C-Profil 50/25/2',
    'C-Profil 90/25/2',
    'Alu-Klemmprofil für Franz',
];

const SPECIAL_PROFILE_NAME = 'Spezialprofile';
const toNum = (v: any): number => (typeof v === 'number' ? v : v ? Number(v) : 0);

/* =========================================================
   Vorschlags-Typen
   ========================================================= */
type DimSuggestionBase = { label: string; score?: number };
type DimSuggestionRectLike = DimSuggestionBase & {
    type: 'Vierkantrohr' | 'L-Profil' | 'U-Profil' | 'C-Profil' | 'T-Profil' | 'H-Profil';
    dims: { HEIGHT: number; WIDTH: number; THICKNESS: number };
};
type DimSuggestionRoundPipe = DimSuggestionBase & {
    type: 'Rundrohr';
    dims: { DIAMETER: number; THICKNESS: number };
};
type DimSuggestionRoundBar = DimSuggestionBase & { type: 'Rundstab'; dims: { DIAMETER: number } };
type DimSuggestionFlat = DimSuggestionBase & { type: 'Flach/Blech'; dims: { WIDTH: number; THICKNESS: number } };
type DimSuggestion = DimSuggestionRectLike | DimSuggestionRoundPipe | DimSuggestionRoundBar | DimSuggestionFlat;

// Import “roh” und hier typisieren, um keine Typ-Abhängigkeit in der Datei zu erzeugen
const HARD_CODED_DIM_SUGGESTIONS: Record<string, DimSuggestion[]> = RAW_SUGGESTIONS as any;

/* ====================== Suggestion-UI ====================== */
type DimSuggestionsPanelProps = {
    profileType?: string;
    onPick: (dims: Partial<Record<DimKey, number>>) => void;
};

const DimSuggestionsPanel: React.FC<DimSuggestionsPanelProps> = ({ profileType, onPick }) => {
    const [query, setQuery] = React.useState('');
    const [showAll, setShowAll] = React.useState(false);

    const base = profileType ? HARD_CODED_DIM_SUGGESTIONS[profileType] || [] : [];
    const sorted = React.useMemo(() => [...base].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)), [base]);
    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return sorted;
        return sorted.filter(
            (s) => s.label.toLowerCase().includes(q) || ('' + (s.score ?? '')).includes(q)
        );
    }, [sorted, query]);

    const visible = showAll ? filtered : filtered.slice(0, 20); // Top 20 default

    if (!profileType || base.length === 0) return null;

    return (
        <div style={{ marginTop: 4, marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                    Vorschläge ({profileType}): {filtered.length} Treffer
                </div>
                <Input
                    size="small"
                    placeholder="Filtern (z. B. 40x10)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ maxWidth: 220 }}
                    allowClear
                />
                <Button size="small" onClick={() => setShowAll((v) => !v)}>
                    {showAll ? 'Top 20 anzeigen' : 'Alle anzeigen'}
                </Button>
            </div>

            <Space size={[6, 6]} wrap>
                {visible.map((s, idx) => (
                    <Tag
                        key={`${s.type}-${s.label}-${idx}`}
                        color="blue"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => {
                            if (s.type === 'Rundstab') onPick({ DIAMETER: (s as any).dims.DIAMETER });
                            else if (s.type === 'Rundrohr')
                                onPick({ DIAMETER: (s as any).dims.DIAMETER, THICKNESS: (s as any).dims.THICKNESS });
                            else if (s.type === 'Flach/Blech')
                                onPick({ WIDTH: (s as any).dims.WIDTH, THICKNESS: (s as any).dims.THICKNESS });
                            else onPick({ HEIGHT: (s as any).dims.HEIGHT, WIDTH: (s as any).dims.WIDTH, THICKNESS: (s as any).dims.THICKNESS });
                        }}
                    >
                        {s.label}
                        {typeof s.score === 'number' ? (
                            <span style={{ opacity: 0.7 }}> · {s.score.toLocaleString('de-DE')} m</span>
                        ) : null}
                    </Tag>
                ))}
            </Space>

            {!showAll && filtered.length > 20 ? (
                <div style={{ marginTop: 6 }}>
                    <Button size="small" type="link" onClick={() => setShowAll(true)}>
                        Mehr anzeigen ({filtered.length - 20} weitere) »
                    </Button>
                </div>
            ) : null}

            <Divider style={{ margin: '8px 0' }} />
        </div>
    );
};

/* ====================== Editor-Modal ====================== */
type EditorProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (row: MaterialReservationItem) => void;
    initial?: MaterialReservationItem | null;
    designations: Designation[];
    materials: Material[];
    profileTypes: ProfileType[];
    uom: Uom;
    title?: string;
};

const MaterialItemEditorModal: React.FC<EditorProps> = ({
                                                            open,
                                                            onClose,
                                                            onSubmit,
                                                            initial,
                                                            designations,
                                                            materials,
                                                            profileTypes,
                                                            uom,
                                                            title = 'Artikel hinzufügen',
                                                        }) => {
    const [form] = Form.useForm();
    const designationVal = Form.useWatch('designation', form);

    const materialByName = (n?: string) => materials.find((m) => m.name === n);
    const profileByName = (n?: string) => profileTypes.find((p) => p.name === n);

    const requiredDimsFor = (profileName?: string): DimKey[] => {
        const name = profileName || '';
        if (name === SPECIAL_PROFILE_NAME) return [];
        if (/Rundrohr/i.test(name)) return ['DIAMETER', 'THICKNESS'];
        if (/Rundstab/i.test(name)) return ['DIAMETER'];
        if (/Flach|Blech/i.test(name)) return ['WIDTH', 'THICKNESS'];
        if (/L-Profil/i.test(name)) return ['HEIGHT', 'WIDTH', 'THICKNESS'];
        if (/U-Profil/i.test(name)) return ['HEIGHT', 'WIDTH', 'THICKNESS'];
        if (/Vierkantrohr/i.test(name)) return ['HEIGHT', 'WIDTH', 'THICKNESS'];
        const fromService = (profileByName(name)?.properties || []).filter((p): p is DimKey => p !== 'UNKNOWN');
        return fromService;
    };

    const orderedDimsFor = (profileName?: string): DimKey[] => {
        const req = requiredDimsFor(profileName);
        const name = profileName || '';
        if (/Rundrohr/i.test(name)) return ['DIAMETER', 'THICKNESS'] as DimKey[];
        if (/Flach|Blech/i.test(name)) return ['WIDTH', 'THICKNESS'] as DimKey[];
        if (/Rundstab/i.test(name)) return ['DIAMETER'] as DimKey[];
        const baseOrder: DimKey[] = ['HEIGHT', 'WIDTH', 'THICKNESS'];
        return baseOrder.filter((k) => req.includes(k));
    };

    // Fläche (mm²)
    const areaMm2 = (row: Partial<MaterialReservationItem>): number | null => {
        const type = row.profileType || '';
        if (type === SPECIAL_PROFILE_NAME) return null;

        const h = row.HEIGHT ?? 0;
        const b = row.WIDTH ?? 0;
        const t = row.THICKNESS ?? 0;
        const D = row.DIAMETER ?? 0;

        const PI = Math.PI;
        if (/Vierkantrohr/i.test(type)) {
            if (h > 0 && b > 0 && t > 0 && h - 2 * t > 0 && b - 2 * t > 0) return h * b - (h - 2 * t) * (b - 2 * t);
            return null;
        }
        if (/Rundrohr/i.test(type)) {
            if (D > 0 && t > 0 && D - 2 * t > 0) return (PI / 4) * (D * D - (D - 2 * t) * (D - 2 * t));
            return null;
        }
        if (/Rundstab/i.test(type)) {
            if (D > 0) return (PI / 4) * D * D;
            return null;
        }
        if (/Flach|Blech/i.test(type)) {
            if (b > 0 && t > 0) return b * t;
            return null;
        }
        if (/L-Profil/i.test(type)) {
            if (h > 0 && b > 0 && t > 0) return t * (h + b - t);
            return null;
        }
        if (/U-Profil/i.test(type)) {
            if (h > 0 && b > 0 && t > 0) return t * (2 * h + b - 2 * t);
            return null;
        }
        return null;
    };

    // kg/m (nur Geometrie + Material, unabhängig von Länge/Stück)
    const weightPerMeterKg = (row: Partial<MaterialReservationItem>, mat?: Material): number | null => {
        if (row.profileType === SPECIAL_PROFILE_NAME) {
            const v = toNum(row.specialWeightPerMeter);
            return v > 0 ? Number(v.toFixed(3)) : null;
        }
        const m = mat ?? materials.find((mm) => mm.name === row.material);
        if (!m) return null;
        const A = areaMm2(row);
        if (!A) return null;
        return Number((m.densityFactorKgPerMPerMm2 * A).toFixed(3));
    };

    // Live-Bindings
    const curMaterial = materialByName(Form.useWatch('material', form));
    const selectedProfileType = Form.useWatch('profileType', form) as string | undefined;
    const reqDims = requiredDimsFor(selectedProfileType);
    const orderedDims = orderedDimsFor(selectedProfileType);

    const liveRow: Partial<MaterialReservationItem> = {
        material: curMaterial?.name,
        materialGrade: Form.useWatch('materialGrade', form),
        profileType: selectedProfileType,
        specialProfileName: Form.useWatch('specialProfileName', form),
        specialWeightPerMeter: Form.useWatch('specialWeightPerMeter', form),
        HEIGHT: Form.useWatch('HEIGHT', form),
        WIDTH: Form.useWatch('WIDTH', form),
        THICKNESS: Form.useWatch('THICKNESS', form),
        DIAMETER: Form.useWatch('DIAMETER', form),
        lengthPerUnit: Form.useWatch('lengthPerUnit', form),
        piecesPerUnit: Form.useWatch('piecesPerUnit', form),
        comment: Form.useWatch('comment', form),
    };
    const liveKgPerM = weightPerMeterKg(liveRow, curMaterial);

    // Initialwerte setzen
    useEffect(() => {
        form.resetFields();
        if (initial) {
            form.setFieldsValue({
                designation: initial.designation,
                material: initial.material,
                materialGrade: initial.materialGrade,
                profileType: initial.profileType,
                specialProfileName: initial.specialProfileName,
                specialWeightPerMeter: initial.specialWeightPerMeter,
                HEIGHT: initial.HEIGHT,
                WIDTH: initial.WIDTH,
                THICKNESS: initial.THICKNESS,
                DIAMETER: initial.DIAMETER,
                lengthPerUnit: initial.lengthPerUnit,
                piecesPerUnit: initial.piecesPerUnit,
                comment: initial.comment,
            });
        }
    }, [initial, open]); // eslint-disable-line

    // Standard “piecesPerUnit” = 1 für alle PCS_* (nicht für Laufmeter)
    useEffect(() => {
        if (open && isPCS(uom)) {
            const current = form.getFieldValue('piecesPerUnit');
            if (!current || Number(current) <= 0) {
                form.setFieldsValue({ piecesPerUnit: 1 });
            }
        }
    }, [open, uom, form]);

    // Handler: Vorschlag übernehmen
    const applyDimSuggestion = (dims: Partial<Record<DimKey, number>>) => {
        const required = new Set(reqDims);
        const nextValues: Partial<Record<DimKey, number | undefined>> = {
            HEIGHT: required.has('HEIGHT') ? dims.HEIGHT : undefined,
            WIDTH: required.has('WIDTH') ? dims.WIDTH : undefined,
            THICKNESS: required.has('THICKNESS') ? dims.THICKNESS : undefined,
            DIAMETER: required.has('DIAMETER') ? dims.DIAMETER : undefined,
        };
        form.setFieldsValue(nextValues);
    };

    return (
        <Modal open={open} onCancel={onClose} title={title} footer={null} width={880} destroyOnClose>
            <Form
                form={form}
                layout="vertical"
                className="mrdm-form-grid tight"
                onValuesChange={(ch) => {
                    if ('profileType' in ch) {
                        if (ch.profileType === SPECIAL_PROFILE_NAME || !ch.profileType) {
                            form.setFieldsValue({ HEIGHT: undefined, WIDTH: undefined, THICKNESS: undefined, DIAMETER: undefined });
                        }
                        const req = requiredDimsFor(ch.profileType);
                        const set = new Set(req);
                        (['HEIGHT', 'WIDTH', 'THICKNESS', 'DIAMETER'] as DimKey[]).forEach((k) => {
                            if (!set.has(k)) form.setFieldValue(k, undefined);
                        });
                    }
                    if ('material' in ch) form.setFieldValue('materialGrade', undefined);
                }}
            >
                {/* IMMER SICHTBAR */}
                <Form.Item name="designation" label="Bezeichnung" rules={[{ required: true, message: 'Pflichtfeld' }]}>
                    <Select placeholder="z. B. Obergurt" allowClear showSearch optionFilterProp="children">
                        {designations.map((d) => (
                            <Option key={d.name} value={d.name}>
                                {d.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="material" label="Material" rules={[{ required: true, message: 'Pflichtfeld' }]}>
                    <Select placeholder="z. B. Aluminium" allowClear showSearch optionFilterProp="children">
                        {materials.map((m) => (
                            <Option key={m.name} value={m.name}>
                                {m.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="materialGrade"
                    label="Materialgüte"
                    rules={curMaterial && curMaterial.materialGrades?.length > 0 ? [{ required: true, message: 'Pflichtfeld' }] : []}
                >
                    <Select
                        placeholder={curMaterial && curMaterial.materialGrades?.length ? 'z. B. S235' : 'Keine Güte'}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!(curMaterial && curMaterial.materialGrades?.length)}
                    >
                        {(curMaterial?.materialGrades || []).map((g) => (
                            <Option key={g} value={g}>
                                {g}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="profileType" label="Profilart" rules={[{ required: true, message: 'Pflichtfeld' }]}>
                    <Select placeholder="z. B. Rundrohr" allowClear showSearch optionFilterProp="children">
                        {profileTypes.map((p) => (
                            <Option key={p.name} value={p.name}>
                                {p.name}
                            </Option>
                        ))}
                        {!profileTypes.find((p) => p.name === SPECIAL_PROFILE_NAME) && (
                            <Option key={SPECIAL_PROFILE_NAME} value={SPECIAL_PROFILE_NAME}>
                                {SPECIAL_PROFILE_NAME}
                            </Option>
                        )}
                    </Select>
                </Form.Item>

                {/* DYNAMISCH NACH PROFILART */}
                {!!selectedProfileType && selectedProfileType !== SPECIAL_PROFILE_NAME && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <DimSuggestionsPanel profileType={selectedProfileType} onPick={applyDimSuggestion} />
                    </div>
                )}

                {/* Maße / Spezialprofilfelder */}
                {!!selectedProfileType &&
                    (selectedProfileType === SPECIAL_PROFILE_NAME ? (
                        <>
                            <Form.Item name="specialProfileName" label="Spezialprofil" rules={[{ required: true, message: 'Profil wählen' }]}>
                                <Select placeholder="Profil auswählen (kg/m manuell eingeben)" allowClear showSearch optionFilterProp="children">
                                    {SPECIAL_PROFILE_OPTIONS.map((n) => (
                                        <Option key={n} value={n}>
                                            {n}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="specialWeightPerMeter"
                                label="Gewicht je Laufmeter (kg/m)"
                                rules={[{ required: true, message: 'Pflichtfeld' }, { type: 'number', min: 0.001, message: 'Wert > 0' }]}
                            >
                                <InputNumber placeholder="z. B. 18" min={0} step={0.01} style={{ width: '100%' }} />
                            </Form.Item>
                        </>
                    ) : (
                        orderedDims.map((k) => (
                            <Form.Item
                                key={k}
                                name={k}
                                label={DIM_LABELS[k]}
                                rules={[{ required: true, message: 'Pflichtfeld' }, { type: 'number', min: 0.1, message: 'Wert > 0' }]}
                            >
                                <InputNumber placeholder="mm" min={0} step={k === 'THICKNESS' || k === 'DIAMETER' ? 0.1 : 1} style={{ width: '100%' }} />
                            </Form.Item>
                        ))
                    ))}

                {/* Länge/Stück + kg/m Vorschau */}
                {!!selectedProfileType && (
                    <>
                        <Form.Item
                            name="lengthPerUnit"
                            label={uom === 'LM' ? 'Länge je Laufmeter (mm)' : 'Länge je Stück (mm)'}
                            rules={[{ required: true, message: 'Pflichtfeld' }, { type: 'number', min: 0.1, message: 'Wert > 0' }]}
                        >
                            <InputNumber placeholder="mm" min={0} step={1} style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item
                            name="piecesPerUnit"
                            // Wichtig: hier jetzt auch "Anzahl ..." (wie gewünscht)
                            label={PIECES_PER_UNIT_EDITOR_LABEL[uom]}
                            rules={[{ required: true, message: 'Pflichtfeld' }, { type: 'number', min: 1, message: 'Mind. 1' }]}
                        >
                            <InputNumber
                                placeholder={
                                    designationVal === 'Obergurt und Untergurt'
                                        ? 'Bitte Menge für Ober und Untergurt angeben'
                                        : isPCS(uom)
                                            ? 'z. B. 1'
                                            : 'z. B. 2'
                                }
                                min={1}
                                step={1}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>

                        <Form.Item label="Gewicht je LFM (kg/m)">
                            <InputNumber value={liveKgPerM ?? undefined} precision={3} placeholder="–" disabled controls={false} style={{ width: '100%' }} />
                        </Form.Item>
                    </>
                )}

                <Form.Item name="comment" label="Kommentar / Bemerkung" rules={[{ max: 2000, message: 'Max. 2000 Zeichen' }]}>
                    <Input.TextArea rows={3} placeholder="Interne Bemerkung zu diesem Artikel" />
                </Form.Item>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={onClose}>Abbrechen</Button>
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={async () => {
                            try {
                                const v = await form.validateFields();
                                const row: MaterialReservationItem = {
                                    id: initial?.id ?? `${Date.now()}`,
                                    designation: v.designation,
                                    material: v.material,
                                    materialGrade: v.materialGrade,
                                    profileType: v.profileType,
                                    specialProfileName: v.specialProfileName,
                                    specialWeightPerMeter: v.specialWeightPerMeter,
                                    HEIGHT: v.HEIGHT,
                                    WIDTH: v.WIDTH,
                                    THICKNESS: v.THICKNESS,
                                    DIAMETER: v.DIAMETER,
                                    lengthPerUnit: v.lengthPerUnit,
                                    piecesPerUnit: v.piecesPerUnit,
                                    comment: v.comment,
                                    weightPerMeter: (() => {
                                        if (v.profileType === SPECIAL_PROFILE_NAME) {
                                            const vv = toNum(v.specialWeightPerMeter);
                                            return vv > 0 ? Number(vv.toFixed(3)) : undefined;
                                        }
                                        const m = materialByName(v.material);
                                        if (!m) return undefined;
                                        const h = v.HEIGHT,
                                            b = v.WIDTH,
                                            t = v.THICKNESS,
                                            D = v.DIAMETER;
                                        const PI = Math.PI;
                                        let A: number | null = null;
                                        if (/Vierkantrohr/i.test(v.profileType)) {
                                            if (h > 0 && b > 0 && t > 0 && h - 2 * t > 0 && b - 2 * t > 0) A = h * b - (h - 2 * t) * (b - 2 * t);
                                        } else if (/Rundrohr/i.test(v.profileType)) {
                                            if (D > 0 && t > 0 && D - 2 * t > 0) A = (PI / 4) * (D * D - (D - 2 * t) * (D - 2 * t));
                                        } else if (/Rundstab/i.test(v.profileType)) {
                                            if (D > 0) A = (PI / 4) * D * D;
                                        } else if (/Flach|Blech/i.test(v.profileType)) {
                                            if (b > 0 && t > 0) A = b * t;
                                        } else if (/L-Profil/i.test(v.profileType)) {
                                            if (h > 0 && b > 0 && t > 0) A = t * (h + b - t);
                                        } else if (/U-Profil/i.test(v.profileType)) {
                                            if (h > 0 && b > 0 && t > 0) A = t * (2 * h + b - 2 * t);
                                        }
                                        if (A == null) return undefined;
                                        return Number((m.densityFactorKgPerMPerMm2 * A).toFixed(3));
                                    })(),
                                };
                                onSubmit(row);
                            } catch {
                                /* AntD zeigt Fehler */
                            }
                        }}
                    >
                        Übernehmen
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};
/* ==================== Ende Editor-Modal =================== */

const MaterialReservationDetailModal: React.FC<Props> = ({ initialState, onSave, onCancel }) => {
    // Options
    const [designations, setDesignations] = useState<Designation[]>([]);
    const [materials, setMaterials] = useState<Material[]>([]);
    const [profileTypes, setProfileTypes] = useState<ProfileType[]>([]);
    const [loading, setLoading] = useState(false);

    // UoM + Gesamtmenge (oben)
    const [uom, setUom] = useState<Uom>('LM');
    const [totalQty, setTotalQty] = useState<number>(0);

    // Items
    const [items, setItems] = useState<MaterialReservationItem[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    // Editor Modal
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorInit, setEditorInit] = useState<MaterialReservationItem | null>(null);
    const isEditing = editingIndex !== null;
    const hasItems = items.length > 0;
    const canChangeUom = !hasItems;

    // Pflicht: es muss eine Gesamtmenge > 0 eingegeben sein, sonst kann nicht gespeichert werden
    // (früher wurde häufig 0 gespeichert). Blockiert das "Speichern"; "Schliessen" bleibt möglich,
    // da es nichts persistiert.
    const canSave = (Number(totalQty) || 0) > 0;

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [dRes, mRes, pRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/getProductDesignations?auth=${AUTH_TOKEN}`),
                    axios.get(`${API_BASE_URL}/getProductMaterials?auth=${AUTH_TOKEN}`),
                    axios.get(`${API_BASE_URL}/getProductProfileTypes?auth=${AUTH_TOKEN}`),
                ]);
                setDesignations(dRes.data || []);
                setMaterials(mRes.data || []);
                setProfileTypes(pRes.data || []);
            } catch {
                message.error('Fehler beim Laden der Konfigurationsdaten.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // initial laden (wenn vorhanden)
    useEffect(() => {
        if (initialState) {
            setUom(initialState.uom);
            setTotalQty(initialState.totalQty);
            setItems(initialState.items || []);
        }
    }, [initialState]);

    const materialByName = (n?: string) => materials.find((m) => m.name === n);

    // Fläche (mm²)
    const areaMm2 = (row: Partial<MaterialReservationItem>): number | null => {
        const type = row.profileType || '';
        if (type === SPECIAL_PROFILE_NAME) return null;

        const h = row.HEIGHT ?? 0;
        const b = row.WIDTH ?? 0;
        const t = row.THICKNESS ?? 0;
        const D = row.DIAMETER ?? 0;

        const PI = Math.PI;
        if (/Vierkantrohr/i.test(type)) {
            if (h > 0 && b > 0 && t > 0 && h - 2 * t > 0 && b - 2 * t > 0) return h * b - (h - 2 * t) * (b - 2 * t);
            return null;
        }
        if (/Rundrohr/i.test(type)) {
            if (D > 0 && t > 0 && D - 2 * t > 0) return (PI / 4) * (D * D - (D - 2 * t) * (D - 2 * t));
            return null;
        }
        if (/Rundstab/i.test(type)) {
            if (D > 0) return (PI / 4) * D * D;
            return null;
        }
        if (/Flach|Blech/i.test(type)) {
            if (b > 0 && t > 0) return b * t;
            return null;
        }
        if (/L-Profil/i.test(type)) {
            if (h > 0 && b > 0 && t > 0) return t * (h + b - t);
            return null;
        }
        if (/U-Profil/i.test(type)) {
            if (h > 0 && b > 0 && t > 0) return t * (2 * h + b - 2 * t);
            return null;
        }
        return null;
    };

    // --- Material-Laufmeter (immer Meter, unabhängig von UoM oben) ---
    const materialRunningMeters = (row: Partial<MaterialReservationItem>): number => {
        const lengthM = toNum(row.lengthPerUnit) / 1000;
        const pcs = toNum(row.piecesPerUnit);
        const qty = totalQty || 0;

        // PCS und PCS_* laufen hier absichtlich in den gleichen Branch.
        return Number((qty * lengthM * pcs).toFixed(3));
    };

    const weightPerMeterKgRow = (row: Partial<MaterialReservationItem>, mat?: Material): number | null => {
        if (row.profileType === SPECIAL_PROFILE_NAME) {
            const v = toNum(row.specialWeightPerMeter);
            return v > 0 ? Number(v.toFixed(3)) : null;
        }
        const m = mat ?? materialByName(row.material);
        if (!m) return null;
        const A = areaMm2(row);
        if (!A) return null;
        return Number((m.densityFactorKgPerMPerMm2 * A).toFixed(3));
    };

    const totalWeightRowKg = (row: Partial<MaterialReservationItem>, mat?: Material): number | null => {
        const kgPerM = weightPerMeterKgRow(row, mat);
        if (kgPerM == null) return null;
        return Number((kgPerM * materialRunningMeters(row)).toFixed(2));
    };

    const fmt = (v: number | null | undefined, digits = 2) => (v == null || Number.isNaN(v) ? '–' : Number(v).toFixed(digits));

    const onDelete = (index: number) => {
        const next = [...items];
        next.splice(index, 1);
        setItems(next);
        if (editingIndex === index) setEditingIndex(null);
        if (next.length === 0) {
            message.info('Alle Artikel entfernt – die Einheit kann wieder geändert werden.');
        }
    };

    // Tabelle
    const columns = useMemo(() => {
        return [
            { title: 'Bezeichnung', dataIndex: 'designation', key: 'designation', width: 220, render: (v: string) => <Tag>{v}</Tag> },
            {
                title: 'Material',
                key: 'material',
                width: 220,
                render: (_: any, r: MaterialReservationItem) => (
                    <Space size={6} wrap>
                        <Tag color="blue">{r.material || '-'}</Tag>
                        {r.materialGrade ? <Tag color="geekblue">{r.materialGrade}</Tag> : null}
                    </Space>
                ),
            },
            {
                title: 'Profilart',
                dataIndex: 'profileType',
                key: 'profileType',
                width: 120,
                render: (v: string, r: MaterialReservationItem) =>
                    v === SPECIAL_PROFILE_NAME && r.specialProfileName ? (
                        <Space size={6}>
                            <Tag color="purple">{v}</Tag>
                            <Tag>{r.specialProfileName}</Tag>
                        </Space>
                    ) : (
                        v
                    ),
            },
            {
                title: 'Abmessungen (mm)',
                key: 'dims',
                render: (_: any, r: MaterialReservationItem) => {
                    const order: DimKey[] = ['HEIGHT', 'WIDTH', 'THICKNESS', 'DIAMETER'];
                    const list = order
                        .map((k) => ({ k, val: (r as any)[k] as number | undefined }))
                        .filter((x) => x.val != null);
                    if (list.length === 0) return '—';
                    return (
                        <div className="mrdm-dims-grid">
                            {list.map(({ k, val }) => (
                                <span key={k} className="mrdm-dim-tag" title={DIM_LABELS[k]}>
                  {k === 'DIAMETER' ? 'Ø' : k === 'HEIGHT' ? 'H' : k === 'WIDTH' ? 'B' : 'S'}: {val} mm
                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                title: isPCS(uom) ? 'Länge/Stück (mm)' : 'Länge/Laufmeter (mm)',
                dataIndex: 'lengthPerUnit',
                key: 'length',
                width: 170,
                render: (v: number) => v ?? '-',
            },
            {
                // WICHTIG: hier jetzt überall "Anzahl ..."
                title: PIECES_PER_UNIT_LABEL[uom],
                dataIndex: 'piecesPerUnit',
                key: 'pcs',
                width: 160,
                render: (v: number) => v ?? '-',
            },
            {
                title: 'Material-Laufmeter',
                key: 'totalQty',
                width: 160,
                render: (_: any, r: MaterialReservationItem) => {
                    const eff = materialRunningMeters(r);
                    const pretty = eff == null ? '–' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(eff);
                    return (
                        <Space size={6}>
                            <span>{pretty}</span>
                            <Tag>LFM</Tag>
                        </Space>
                    );
                },
            },
            {
                title: 'Gewicht (kg)',
                key: 'w',
                width: 190,
                render: (_: any, r: MaterialReservationItem) => {
                    const mat = materialByName(r.material);
                    const kgPerM = weightPerMeterKgRow(r, mat);
                    const total = totalWeightRowKg(r, mat);
                    return (
                        <div>
                            <div style={{ fontWeight: 600 }}>{fmt(total)}</div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>je LFM Material: {fmt(kgPerM, 3)}</div>
                        </div>
                    );
                },
            },
            {
                title: 'Aktionen',
                key: 'act',
                width: 110,
                render: (_: any, __: MaterialReservationItem, index: number) => (
                    <Space>
                        <Tooltip title="Bearbeiten">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setEditingIndex(index);
                                    setEditorInit(items[index]);
                                    setEditorOpen(true);
                                }}
                            />
                        </Tooltip>
                        <Popconfirm title="Eintrag löschen?" okText="Löschen" cancelText="Abbrechen" onConfirm={() => onDelete(index)}>
                            <Button type="text" icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, materials, profileTypes, totalQty, uom]);

    // Zusammenfassung für Save
    const computeSummary = (): MaterialReservationSavePayload => {
        const materialLm = items.reduce((sum, r) => sum + materialRunningMeters(r), 0);
        const totalWeightKg = items.reduce((sum, r) => {
            const mat = materialByName(r.material);
            const w = totalWeightRowKg(r, mat) || 0;
            return sum + w;
        }, 0);

        const kgPerLm = materialLm > 0 ? Number((totalWeightKg / materialLm).toFixed(2)) : 0;

        const state: MaterialReservationState = { uom, totalQty, items };

        return {
            state,
            runningMeters: Number((totalQty || 0).toFixed(2)),
            materialRunningMeters: Number(materialLm.toFixed(2)),
            kgPerLm,
            totalWeightKg: Number(totalWeightKg.toFixed(2)),
        };
    };

    return (
        <div className="mrdm-compact-wrap">
            {/* Kopf: UoM + Gesamtmenge */}
            <Card className="mrdm-card" bodyStyle={{ padding: 10, marginBottom: 8 }}>
                <Space size={12} wrap>
                    <Form layout="inline">
                        <Form.Item label="Einheit">
                            <Tooltip title={hasItems ? 'Einheit kann erst geändert werden, wenn alle Artikel entfernt sind.' : undefined}>
                                <Select<Uom>
                                    value={uom}
                                    onChange={(val) => {
                                        if (!canChangeUom) {
                                            message.warning('Einheit kann erst geändert werden, wenn alle Artikel entfernt sind.');
                                            return;
                                        }
                                        setUom(val);
                                    }}
                                    disabled={!canChangeUom}
                                    style={{ width: 220 }}
                                    dropdownRender={(menu) => menu}
                                >
                                    <Option value="LM">Laufmeter</Option>
                                    <OptGroup label={<span style={{ fontWeight: 700 }}>Stück</span>}>
                                        <Option value="PCS">Stück</Option>
                                        <Option value="PCS_STOREY">Geschoss</Option>
                                        <Option value="PCS_FACADE">Fassade</Option>
                                        <Option value="PCS_BUILDING">Gebäude</Option>
                                    </OptGroup>
                                </Select>
                            </Tooltip>
                        </Form.Item>

                        <Form.Item
                            label={TOTAL_QTY_LABEL[uom]}
                            required
                            validateStatus={canSave ? undefined : 'error'}
                            help={canSave ? undefined : 'Pflichtfeld – Wert grösser als 0 eingeben'}
                        >
                            <InputNumber
                                min={0}
                                step={isPCS(uom) ? 1 : 0.1}
                                value={totalQty}
                                status={canSave ? undefined : 'error'}
                                onChange={(v) => setTotalQty(Number(v) || 0)}
                                style={{ width: 220 }}
                            />
                        </Form.Item>
                    </Form>
                </Space>
            </Card>

            {/* Tabelle + Add */}
            <Card className="mrdm-card table-card" bodyStyle={{ padding: 10 }} loading={loading}>
                <div className="mrdm-list-head">
                    <Title level={5} className="mrdm-list-title">
                        Zusammengestellte Artikel
                    </Title>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingIndex(null); setEditorInit(null); setEditorOpen(true); }}>
                        Artikel
                    </Button>
                </div>
                <Table
                    size="small"
                    className="mrdm-table"
                    dataSource={items}
                    columns={columns as any}
                    rowKey="id"
                    pagination={{ pageSize: 6, showSizeChanger: false }}
                    locale={{ emptyText: 'Noch keine Artikel. Mit „Artikel“ hinzufügen.' }}
                />
            </Card>

            {/* Footer-Aktionen */}
            <Card className="mrdm-card actions-card" bodyStyle={{ padding: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Button
                        htmlType="button"
                        onClick={() => {
                            if (typeof onCancel === 'function') {
                                onCancel();
                                return;
                            }
                            const modal = (document.activeElement as HTMLElement)?.closest?.('.ant-modal') ?? document.querySelector('.ant-modal');
                            const closeBtn = modal?.querySelector?.('.ant-modal-close') as HTMLButtonElement | null;
                            closeBtn?.click();
                        }}
                    >
                        Schliessen
                    </Button>
                    <Tooltip title={canSave ? undefined : `${TOTAL_QTY_LABEL[uom]} muss grösser als 0 sein`}>
                        <Button
                            type="primary"
                            disabled={!canSave}
                            onClick={() => {
                                if (!canSave) {
                                    message.error(`Bitte zuerst ${TOTAL_QTY_LABEL[uom]} (grösser als 0) eingeben.`);
                                    return;
                                }
                                const payload = computeSummary();
                                onSave?.(payload);
                                message.success('Material-Reservation übernommen.');
                            }}
                        >
                            Speichern
                        </Button>
                    </Tooltip>
                </Space>
            </Card>

            {/* Editor-Modal */}
            <MaterialItemEditorModal
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                onSubmit={(row) => {
                    if (isEditing && editingIndex !== null) {
                        const copy = [...items];
                        copy[editingIndex] = row;
                        setItems(copy);
                        message.success('Eintrag aktualisiert.');
                    } else {
                        setItems((prev) => [row, ...prev]);
                        message.success('Eintrag hinzugefügt.');
                    }
                    setEditorOpen(false);
                    setEditingIndex(null);
                    setEditorInit(null);
                }}
                initial={editorInit}
                designations={designations}
                materials={materials}
                profileTypes={profileTypes}
                uom={uom}
                title={isEditing ? 'Artikel bearbeiten' : 'Artikel hinzufügen'}
            />
        </div>
    );
};

export default MaterialReservationDetailModal;
